import React, { useCallback, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  resetState,
  selectReferralPacket,
} from "../../../../domain/Referral/loader"
import { PreferredLocation } from "./PreferredLocation"
import { CaseInformation } from "./CaseInformation"
import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import appInsightsLogger from "../../../logging/appInsightsLogger"
import { apiService } from "../../apiService"
import { referralTableDataEnums } from "../../../../domain/Referral/tableDataEnums"
import {
  ReferralContactInfo,
  ReferralInsurance,
} from "../../../../domain/Referral/schema"
import { clearHandoffCaseLocalStorage } from "../../utils"
import { resolveSupervisedCounselingRate } from "../../../../features/referrals/utils"
import { errorHandlerUpdated } from "../../../errors/errorSlice"
import ConfirmationModal from "../../../practiceProfile/components/ConfirmationModal"
import { interpolate } from "../../../../domain/CMS/utils"
import {
  selectIsAROrEapResearch,
  selectIsCareManager,
} from "../../../../domain/Account/userSlice"
import Spinner from "../Spinner"
import { SemanticHeader } from "../SemanticHeader"
import { Modal } from "../Modal"

interface RequestReferralModalProps {
  isOpen: boolean
  isUnderSupervision: boolean
  locationData: {
    careType: string
    locationId: string | null
    officeLocation: string
    practice: string
    provider: string
  }
  onClose: () => void
  practiceId: string
  providerId: string
  hasPracticeLiabilityInsurance: boolean
  hasProviderLiabilityInsurance: boolean
  hasProviderLicense: boolean
  hasProviderDisclosure: boolean
}

interface ReferralSubContainerProps {
  children: React.ReactNode
  title: string
}

const ReferralSubContainer: React.FC<ReferralSubContainerProps> = ({
  children,
  title,
}) => {
  return (
    <div className="pt-4 pb-6">
      <SemanticHeader
        text={title}
        type="sectionHeader"
        headerLevel="h3"
        styleOverrides="mt-0 mb-2 font-bold text-font-blue"
      />
      {children}
    </div>
  )
}

export const HR_REFERRED_BY_CODES = ["CR_HR", "CR_MED", "CR_SUPV"]
export const RequestReferralModal: React.FC<RequestReferralModalProps> = ({
  isOpen,
  isUnderSupervision,
  onClose,
  locationData,
  practiceId,
  providerId,
  hasPracticeLiabilityInsurance,
  hasProviderLiabilityInsurance,
  hasProviderLicense,
  hasProviderDisclosure,
}) => {
  const dispatch = useDispatch()
  // the sending state is used to show a loading spinner while the referral request is being processed
  const [sending, setSending] = useState(false)
  const [isRequestReferralModalOpen, setIsRequestReferralModalOpen] =
    useState<boolean>(isOpen)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false)
  const [maxSessionLimit, setMaxSessionLimit] = useState<number | undefined>(
    undefined,
  )
  const [computedReferralSessionLimit, setComputedReferralSessionLimit] =
    useState<number | null>(null)
  const [showReferralSessionLimitError, setShowReferralSessionLimitError] =
    useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [sendReferralButtonPressed, setSendReferralButtonPressed] =
    useState<boolean>(false)
  const isAROrEapResearch = useSelector(selectIsAROrEapResearch)
  const isCareManager = useSelector(selectIsCareManager)
  const canOverrideSessionLimit = isAROrEapResearch && isCareManager

  const {
    referralPacket,
    loading,
    error: apiErrorDetails,
  } = useSelector(selectReferralPacket)

  const handleClose = useCallback(() => {
    onClose()
    dispatch(resetState())
  }, [onClose, dispatch])

  const {
    cms: cmsContent,
    isLoading,
    isReady,
    hasError,
  } = useCMSReady([
    "common",
    "requestReferralModal",
    "referralPacketModal",
    "referrerCodeMap",
  ])

  const commonContent = cmsContent?.common
  const referralContent = cmsContent?.requestReferralModal
  const referralPacketContent = cmsContent.referralPacketModal!

  const {
    caseID = 0,
    memberID = 0,
    caseReferredByCode = "",
    memberFirstName = "",
    memberLastName = "",
    memberGender = "",
    memberDateOfBirth = null,
    workAddress1 = "",
    workAddress2 = "",
    city = "",
    state = "",
    zip = "",
    eapMaxSessionLimit = 0,
    completedSessionCount = 0,
    isSecure = false,
    memberPreferredLanguageCode = "",
    riskCode = "",
    attendees = "",
    presentingProblem = "",
    organizationName = "",
    isConcernedAboutAlcohol = false,
    isConcernedAboutDrugs = false,
    isMemberWorried = false,
    isMemberHarmful = false,
    isMemberHarmfulToSelf = false,
    isMemberHarmfulToOthers = false,
    harmfulDetails = "",
    concernDetails = "",
    worriedDetails = "",
    isKnoxKeane = false, //TODO this is spelled incorrectly in the Memphis API. Fix this once it is corrected.
    clinicalImpressions = "",
    contactInfo = referralPacket?.contactInfo || ([] as ReferralContactInfo[]),
    insurances = referralPacket?.insurances || ([] as ReferralInsurance[]),
  } = referralPacket || {}

  const referralSessionLimit =
    computedReferralSessionLimit ??
    eapMaxSessionLimit - (completedSessionCount ?? 0)

  const handleSubmit = async () => {
    setSendReferralButtonPressed(true)

    if (
      maxSessionLimit !== undefined &&
      (maxSessionLimit <= 0 || maxSessionLimit > 25)
    ) {
      setShowReferralSessionLimitError(true)
      return
    }

    if (!hasPracticeLiabilityInsurance && !hasProviderLiabilityInsurance) {
      setError(referralContent?.requestReferralProviderLiabilityError || "")
      return
    }

    if (!hasProviderLicense) {
      setError(referralContent?.requestReferralProviderLicenseError || "")
      return
    }

    if (!hasProviderDisclosure) {
      setError(referralContent?.requestReferralProviderDisclosureError || "")
      return
    }

    if (referralSessionLimit < 1 && maxSessionLimit === undefined) {
      if (canOverrideSessionLimit) {
        setShowReferralSessionLimitError(true)
      } else {
        setError(referralContent?.referralSessionLimitZeroError || "")
      }
      return
    }

    setError(null)
    setSending(true)
    const isHRMandated = HR_REFERRED_BY_CODES.some(
      (code) => caseReferredByCode === code,
    )
    const source = isHRMandated ? "HR/Manager" : "Self (phone)"
    const isHighRisk = !!(riskCode && riskCode !== "RI_LR")
    const date = new Date()
    const createdDate = date.toISOString()
    // set the expire date to environment variable REFERRAL_EXPIRATION_DAYS days from now, default to 10 days if not set
    const parsedExpirationDays = parseInt(window._env_.REFERRAL_EXPIRATION_DAYS)
    const referralExpirationDays = Number.isNaN(parsedExpirationDays)
      ? 10
      : parsedExpirationDays
    date.setDate(date.getDate() + referralExpirationDays)
    const expireDate = date.toISOString()
    const practiceActiveRate =
      await apiService.practice.get.PRACTICE_ACTIVE_RATE({
        practiceId,
      })

    let counselingRate = practiceActiveRate.rateValue
    if (isUnderSupervision) {
      counselingRate = await resolveSupervisedCounselingRate(
        practiceId,
        counselingRate,
      )
    }

    const getPreferredContact = () => {
      // Check for a primary phone number first
      const primaryPhone = contactInfo.find(
        (contact) =>
          !contact.doNotUse &&
          contact.primaryContactInfo &&
          !isEmail(contact.contactInfo),
      )
      if (primaryPhone) return "phone"

      // If no primary phone, check for a primary email
      const primaryEmail = contactInfo.find(
        (contact) =>
          !contact.doNotUse &&
          contact.primaryContactInfo &&
          isEmail(contact.contactInfo),
      )
      if (primaryEmail) return "email"

      // If neither, return empty string
      return ""
    }

    const getEmail = () => {
      // Try to find primary contact info first
      let contact = contactInfo.find(
        (contact) =>
          !contact.doNotUse &&
          contact.primaryContactInfo &&
          isEmail(contact.contactInfo),
      )?.contactInfo
      if (!contact) {
        contact =
          contactInfo.find(
            (contact) => !contact.doNotUse && isEmail(contact.contactInfo),
          )?.contactInfo ?? ""
      }
      return contact
    }

    const getPhoneInfo = () => {
      // Try to find primary contact info first
      let contact = contactInfo.find(
        (contact) =>
          !contact.doNotUse &&
          contact.primaryContactInfo &&
          !isEmail(contact.contactInfo),
      )
      if (!contact) {
        contact = contactInfo.find(
          (contact) => !contact.doNotUse && !isEmail(contact.contactInfo),
        )
      }
      if (!contact) return { phone: "", type: "" }
      let type = ""
      switch (contact.locationTypeCode) {
        case "LT_HOME":
          type = referralPacketContent.phoneTypeHome
          break
        case "LT_WORK":
          type = referralPacketContent.phoneTypeWork
          break
        case "LT_MOBILE":
          type = referralPacketContent.phoneTypeMobile
          break
        default:
          type = ""
      }
      return { phone: contact.contactInfo, type }
    }

    const shouldAllowCall = () => {
      if (!contactInfo || contactInfo.length === 0) {
        return true
      }
      return contactInfo.some((contact) => !contact.doNotUse)
    }

    const getInsuranceCarrier = () => {
      let insuranceCarrierString = ""
      if (insurances.length > 0) {
        // Map through all insurances with carrier names and join them with commas
        insuranceCarrierString = insurances
          .filter((insurance) => insurance.carrierName)
          .map((insurance) => insurance.carrierName)
          .join(", ")
      }
      return insuranceCarrierString
    }
    const phoneInfo = getPhoneInfo()
    const memberIntakeSnapshotData = referralPacket?.memberIntake?.hasM3Data
      ? {
          hasM3Data: true,
          memberIntakeStateCode: referralPacket.memberIntake.stateCode ?? null,
          memberIntakeZipCode: referralPacket.memberIntake.zipCode ?? null,
          memberIntakeWhoNeedsCare:
            referralPacket.memberIntake.whoNeedsCare ?? null,
          memberIntakePrimaryIssue:
            referralPacket.memberIntake.primaryIssue ?? null,
          memberIntakePrimaryIssueOtherText:
            referralPacket.memberIntake.primaryIssueOtherText ?? null,
          memberIntakeSecondaryIssue:
            referralPacket.memberIntake.secondaryIssue ?? null,
          memberIntakeSecondaryIssueOtherText:
            referralPacket.memberIntake.secondaryIssueOtherText ?? null,
          memberIntakePhq4ResultText:
            referralPacket.memberIntake.phq4ResultText ?? null,
          memberIntakePhq4ResultValue:
            referralPacket.memberIntake.phq4ResultValue ?? null,
          memberIntakePhq4Answers: referralPacket.memberIntake.phq4Answers,
        }
      : {
          hasM3Data: false,
        }
    const referralIntakeData = {
      maxAllowedSessions: eapMaxSessionLimit,
      receivedDateTime: createdDate,
      referredBy: caseReferredByCode,
      preferredLanguage: memberPreferredLanguageCode,
      memberFirstName,
      memberLastName,
      gender: memberGender ?? "",
      dateOfBirth: memberDateOfBirth?.split("T")[0] ?? null,
      phone: phoneInfo.phone,
      phoneType: phoneInfo.type,
      email: getEmail(),
      preferredContact: getPreferredContact(),
      allowCall: shouldAllowCall(),
      organizationName,
      insurance: getInsuranceCarrier(),
      isSecure,
      workAddress1: workAddress1 ?? "",
      workAddress2: workAddress2 ?? "",
      workCity: city ?? "",
      workState: state ?? "",
      workZip: zip ?? "",
      riskCode,
      attendees: attendees ?? "",
      presentingProblem,
      isMemberWorried,
      isMemberHarmful,
      isHarmfulToSelf: isMemberHarmfulToSelf,
      isHarmfulToOthers: isMemberHarmfulToOthers,
      harmfulDetails: harmfulDetails ?? "",
      isMemberConcerned: isConcernedAboutAlcohol || isConcernedAboutDrugs,
      isConcernedAboutAlcohol,
      isConcernedAboutDrugs,
      concernDetails: concernDetails ?? "",
      worriedDetails: worriedDetails ?? "",
      isKnoxKeene: isKnoxKeane, //TODO this is spelled incorrectly in the Memphis API. Fix this once it is corrected.
      clinicalImpressions: clinicalImpressions ?? "",
      ...memberIntakeSnapshotData,
      referralSource: source,
    }

    const referralData = {
      caseNumber: caseID,
      providerId: providerId,
      practiceId: practiceId,
      preferredLocationId: locationData.locationId,
      portalMemberId: memberID,
      counselingRate: counselingRate,
      isUnderSupervision: isUnderSupervision,
      maxSessionCount:
        maxSessionLimit ?? eapMaxSessionLimit - (completedSessionCount ?? 0),
      source: source,
      isMandated: isHRMandated,
      isHighRisk: isHighRisk,
      isSecureCase: !!isSecure,
      description: presentingProblem,
      attendees: attendees ?? "",
      referralStatusDisplayId: referralTableDataEnums.referralStatuses.NEW,
      createdDateTime: createdDate,
      expireDateTime: expireDate,
      referralTreatmentDisplayIds: [],
      referralIntake: referralIntakeData,
      isSessionLimitOverriddenOnCreate:
        maxSessionLimit !== undefined ? true : undefined,
    }

    try {
      await apiService.referral.post.REFERRALS(referralData)
      setIsRequestReferralModalOpen(false)
      setIsConfirmationModalOpen(true)
    } catch (error) {
      setIsRequestReferralModalOpen(false)
      onConfirm()
      appInsightsLogger.traceError(error)
      dispatch(
        errorHandlerUpdated({
          errorBanner: {
            isVisible: true,
            title: commonContent?.errorBannerInternalServerErrorTitle || "",
            content: commonContent?.errorBannerInternalServerErrorContent || "",
          },
        }),
      )
    }
  }

  const onConfirm = () => {
    setIsConfirmationModalOpen(false)
    onClose()
    setSending(false)
    setShowReferralSessionLimitError(false)
    dispatch(resetState())
    clearHandoffCaseLocalStorage()
  }

  const handleSessionLimitChange = (sessionLimit: string) => {
    if (
      sessionLimit !== "" &&
      (Number(sessionLimit) === 0 || Number(sessionLimit) > 25) &&
      sendReferralButtonPressed === true
    ) {
      setShowReferralSessionLimitError(true)
    } else {
      setShowReferralSessionLimitError(false)
    }
    if (sessionLimit === "") {
      setMaxSessionLimit(undefined)
    } else {
      setMaxSessionLimit(Number(sessionLimit))
    }
  }

  const isEmail = (value: string | null | undefined): boolean => {
    if (!value) return false
    // Basic check: contains '@' and a dot after '@', and ends with a dot-suffix
    const atIndex = value.indexOf("@")
    if (atIndex < 1) return false
    const dotIndex = value.lastIndexOf(".")
    if (dotIndex < atIndex + 2) return false
    if (dotIndex === value.length - 1) return false
    return true
  }

  if (isLoading) {
    return <Spinner />
  } else if (hasError || !isReady) {
    const errorMessage = `RequestReferralModal -- Error loading CMS content`
    appInsightsLogger.traceError(errorMessage)
    throw new Error(errorMessage)
  }
  return (
    <div>
      <Modal
        text={{
          cancelButton: referralContent?.requestReferralModalCancelButtonLabel,
          submitButton:
            referralContent?.requestReferralModalSendReferralButtonLabel,
          title: referralContent?.requestReferralModalTitle || "",
        }}
        isOpen={isRequestReferralModalOpen}
        onClose={handleClose}
        submitButtonType="primary"
        onCancel={handleClose}
        panelStyleOverrides="w-full"
        justifyButtons="space-around"
        submitButtonRootStyleOverrides="ml-5"
        onSubmit={
          !apiErrorDetails && !loading && referralPacket
            ? handleSubmit
            : undefined
        }
        titleStyleOverrides="text-lg"
      >
        <div className="mb-2">
          <ReferralSubContainer
            title={referralContent?.preferredLocationTitle || ""}
          >
            <PreferredLocation locationData={locationData} />
          </ReferralSubContainer>
          <ReferralSubContainer
            title={referralContent?.caseInformationTitle || ""}
          >
            <CaseInformation
              sending={sending}
              handleSessionLimitChange={handleSessionLimitChange}
              showReferralSessionLimitError={showReferralSessionLimitError}
              caseSessionLimit={eapMaxSessionLimit}
              onReferralSessionLimitChange={setComputedReferralSessionLimit}
            />
          </ReferralSubContainer>
        </div>
        {error && (
          <div role="alert" className="error-text mt-2">
            {error}
          </div>
        )}
      </Modal>
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onCancel={onConfirm}
        onConfirm={onConfirm}
        hideIconAndTitle={true}
        hideCancelButton={true}
        usePrimaryButtonType={true}
        confirmationText={interpolate(
          referralContent?.requestReferralSuccessConfirmationText,
          { providerName: locationData.provider },
        )}
        confirmButtonText={commonContent?.ok}
      />
    </div>
  )
}
